<template lang="pug">
div
  .suspended-wrap(v-if="session.loggedIn")
    Card.py-4(class="border border-border/70 bg-card/80 text-card-foreground shadow-lg backdrop-blur")
      CardHeader(class="gap-y-2")
        .flex.items-center.gap-3
          Icon(name="heroicons:exclamation-triangle" class="h-7 w-7 text-amber-500")
          div
            h1.text-2xl.font-semibold {{ t('errors.account_suspended.title') }}
            p.text-muted-foreground.text-sm {{ t('errors.account_suspended.message') }}
    Card.py-5(class="mt-6 border border-border/70 bg-card/80 text-card-foreground shadow-lg backdrop-blur" v-if="!user.extra?.tk_suspend")
      CardHeader(class="gap-y-2")
        div(class="flex items-center gap-3")
          Icon(name="heroicons:lifebuoy" class="h-6 w-6 text-sky-500")
          div
            h2(class="text-xl font-semibold") {{ t('suspended_page.request_title') }}
            p(class="text-sm text-muted-foreground") {{ t('suspended_page.request_description') }}
      CardContent
        div(class="grid gap-6")
          div(class="gap-y-3 pb-4 border-b border-border/60")
            div(class="flex items-center gap-2")
              span(class="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold") 1
              div
                p(class="font-medium") {{ t('suspended_page.devices_title') }}
                p(class="text-xs text-muted-foreground") {{ t('suspended_page.devices_hint') }}
                p(class="text-xs text-muted-foreground") {{ t('suspended_page.device_id_helper') }}
            div(class="flex items-center gap-2 flex-wrap")
              Input(
                class="flex-1 min-w-[240px]"
                v-model="newDeviceId"
                :placeholder="t('suspended_page.device_id_placeholder')"
                @keyup.enter.prevent="addDeviceId"
              )
              Button(size="sm" variant="outline" @click="addDeviceId" :disabled="!newDeviceId.trim()") {{ t('suspended_page.add_device') }}
            div(class="flex flex-wrap gap-2")
              span(v-for="id in selectedDeviceIds" :key="id" class="inline-flex items-center rounded-full border border-border/70 bg-background px-2 py-1 text-[11px] gap-1")
                span {{ id }}
                Button(size="icon" variant="ghost" class="h-4 w-4" @click="removeDeviceId(id)") ×
            div
              div(class="flex flex-wrap gap-3")
                img(
                  :src="`${suspendedImageBase}/${locale === 'en' ? 'client-suspended-en.png' : 'client-suspended-zh.png'}`"
                  :alt="locale === 'en' ? 'Desktop app suspended prompt (EN)' : '桌面客户端停用提示（中文）'"
                  class="w-full max-w-[320px] md:max-w-none md:flex-1 rounded-md border border-border/70 bg-black/5 object-contain max-h-60"
                  loading="lazy"
                )
                img(
                  :src="`${suspendedImageBase}/${locale === 'en' ? 'client-help-suspended-en.png' : 'client-help-suspended-zh.png'}`"
                  :alt="locale === 'en' ? 'How to copy device ID (EN)' : '如何复制设备 ID（中文）'"
                  class="w-full max-w-[320px] md:max-w-none md:flex-1 rounded-md border border-border/70 bg-black/5 object-contain max-h-60"
                  loading="lazy"
                )
          div(class="gap-y-3 pb-4 border-b border-border/60")
            div(class="flex items-center gap-2")
              span(class="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold") 2
              div
                p(class="font-medium") {{ t('suspended_page.phone_title') }}
                p(class="text-xs text-muted-foreground") {{ t('suspended_page.phone_hint') }}
            div(class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4")
              RadioGroup(v-model="newPhoneBrand" class="flex flex-wrap gap-3")
                label(class="inline-flex items-center gap-2 rounded-lg border border-border/70 bg-background px-3 py-2 text-sm cursor-pointer")
                  RadioGroupItem(value="iPhone" aria-label="iPhone")
                  span iPhone
                label(class="inline-flex items-center gap-2 rounded-lg border border-border/70 bg-background px-3 py-2 text-sm cursor-pointer")
                  RadioGroupItem(value="Android" aria-label="Android")
                  span Android
              div(class="flex flex-1 items-center gap-2")
                Input(
                  class="flex-1 min-w-[160px]"
                  v-model="newPhoneModel"
                  :placeholder="t('suspended_page.model_placeholder')"
                  autocomplete="device-model"
                  @keyup.enter.prevent="addPhoneEntry"
                )
                Button(size="sm" variant="outline" @click="addPhoneEntry" :disabled="!newPhoneBrand.trim() || !newPhoneModel.trim()") {{ t('suspended_page.add_phone') }}
            div(class="flex flex-wrap gap-2")
              span(v-for="(entry, idx) in phoneDevices" :key="entry.manufacturer + entry.model + idx" class="inline-flex items-center rounded-full border border-border/70 bg-background px-2 py-1 text-[11px] gap-1")
                span {{ entry.manufacturer }} {{ entry.model }}
                Button(size="icon" variant="ghost" class="h-4 w-4" @click="removePhoneEntry(idx)") ×
          div(class="gap-y-3 pb-4 border-b border-border/60")
            div(class="flex items-center gap-2")
              span(class="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold") 3
              div
                p(class="font-medium") {{ t('suspended_page.home_city_section_title') }}
                p(class="text-xs text-muted-foreground") {{ t('suspended_page.home_city_required_hint') }}
            div(class="grid gap-2")
              Input#home_city(
                v-model="homeCity"
                :placeholder="t('suspended_page.home_city_placeholder')"
                autocomplete="address-level2"
              )
          div(class="gap-y-2")
            Label(for="notes") {{ t('suspended_page.notes_label') }}
            Textarea#notes(v-model="deviceNotes" :placeholder="t('suspended_page.notes_placeholder')" rows="3")
            div(class="flex items-center justify-between flex-wrap gap-3")
            p(class="text-xs text-destructive" v-if="submitError" v-html="submitError")
            div(class="flex flex-col gap-2 text-xs text-muted-foreground")
              label(class="flex items-start gap-2")
                Checkbox(id="tos_read" v-model="tosRead")
                span
                  | {{ t('suspended_page.tos_label_read') }}
                  |  
                  NuxtLink(to="/terms-of-service" class="text-primary underline" target="_blank") {{ t('tos') }}
              label(class="flex items-start gap-2")
                Checkbox(id="tos_self" v-model="tosSelf")
                span {{ t('suspended_page.tos_label_self') }}
              label(class="flex items-start gap-2")
                Checkbox(id="tos_violation" v-model="tosViolation")
                span {{ t('suspended_page.tos_label_violation') }}
            Button.w-full.mt-4(class="ml-auto" :loading="submitLoading" :disabled="submitLoading" @click="submitRequest")
              | {{ t('suspended_page.submit_button') }}
  div(class="md:w-[800px] w-full")
    Card(class="w-full mt-6 border border-border/70 bg-card/80 text-card-foreground shadow-lg backdrop-blur" v-if="!session.loggedIn")
      CardHeader(class="gap-y-2")
        div(class="flex items-center gap-3")
          Icon(name="heroicons:lock-closed" class="h-6 w-6 text-amber-500")
          div
            h2(class="text-xl font-semibold") {{ t('suspended_page.login_required_title') }}
          p(class="text-sm text-muted-foreground") {{ t('suspended_page.login_required_desc') }}
      CardContent(class="md:p-4 p-1")
        SignupLoginForm(v-model="loginState" :shadow="true")
</template>

<script setup lang="ts">
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'

const i18n = useI18n()
const { t } = i18n
const locale = computed(() => i18n.locale.value)
const route = useRoute()
const dayjs = useDayjs()
const user = useUser()
const session = useVisitorSession()
const api = useAPI()
const loginState = ref('login')
const selectedDeviceIds = ref<string[]>([])
const newDeviceId = ref('')
const phoneDevices = ref<{ manufacturer: string; model: string }[]>([])
const newPhoneBrand = ref('')
const newPhoneModel = ref('')
const homeCity = ref('')
const deviceNotes = ref('')
const submitLoading = ref(false)
const submitError = ref('')
const suspendedImageBase =
  'https://alicloud-assets.oss-accelerate.aliyuncs.com/img/suspended'
const tosRead = ref(false)
const tosSelf = ref(false)
const tosViolation = ref(false)

const redirectTarget = computed(() => {
  const fromQuery = route.query.from
  if (typeof fromQuery === 'string' && fromQuery && fromQuery !== route.path) {
    return fromQuery
  }
  return '/dashboard'
})

const refreshing = ref(false)
const reloadError = ref<string | null>(null)
const lastChecked = computed(() => {
  const ts = session.updated_at || (user as any)?.updated_at || ''
  if (!ts) return ''
  try {
    return dayjs(ts).format('YYYY-MM-DD HH:mm:ss')
  } catch {
    return ''
  }
})

const isSuspended = computed(() => !session.realAdmin && !!user.extra?.suspend)
const redirectedAway = ref(false)

// if (import.meta.client) {
//   watchEffect(() => {
//     if (!isSuspended.value && !redirectedAway.value) {
//       redirectedAway.value = true
//       navigateTo(redirectTarget.value)
//     }
//   })
// }

const addDeviceId = () => {
  const val = newDeviceId.value.trim()
  if (!val) return
  if (!selectedDeviceIds.value.includes(val)) {
    selectedDeviceIds.value.push(val)
  }
  newDeviceId.value = ''
}

const removeDeviceId = (id: string) => {
  selectedDeviceIds.value = selectedDeviceIds.value.filter((item) => item !== id)
}

const addPhoneEntry = () => {
  const brand = newPhoneBrand.value.trim()
  let model = newPhoneModel.value.trim()
  if (!brand || !model) return
  if (model.toLowerCase().startsWith(brand.toLowerCase())) {
    const trimmed = model.slice(brand.length).trim()
    model = trimmed || model
  }
  phoneDevices.value.push({ manufacturer: brand, model })
  newPhoneBrand.value = ''
  newPhoneModel.value = ''
}

const removePhoneEntry = (idx: number) => {
  phoneDevices.value.splice(idx, 1)
}

const brandMatchers: Record<string, string[]> = {
  iPhone: ['iphone', 'ios', 'ipad'],
  Android: [
    'android',
    'galaxy',
    'pixel',
    'huawei',
    '华为',
    'honor',
    '荣耀',
    'xiaomi',
    '小米',
    'mi ',
    'redmi',
    '红米',
    'poco',
    'oppo',
    '欧珀',
    'oneplus',
    '一加',
    'vivo',
    'iqoo',
    'iqoо',
    'realme',
    'meizu',
    '魅族',
    'lenovo',
    '联想',
    'moto',
    '中兴',
    'zte',
    'nubia',
    '努比亚',
    '黑鲨',
  ],
}

watch(
  () => newPhoneModel.value.trim().toLowerCase(),
  (val) => {
    const setBrandFromModel = () => {
      if (brandMatchers.iPhone!.some((k) => val.startsWith(k))) {
        newPhoneBrand.value = 'iPhone'
        return
      }
      if (brandMatchers.Android!.some((k) => val.startsWith(k))) {
        newPhoneBrand.value = 'Android'
      }
    }
    setBrandFromModel()
  },
  { flush: 'post' },
)

const refreshAccountStatus = async () => {
  if (refreshing.value) return
  refreshing.value = true
  reloadError.value = null
  try {
    const sessionData = await session.getSession(true)
    const userId = sessionData?.user_id || session.user_id
    if (userId && userId !== -1) {
      const freshUser = await user.getUserInfo({ id: userId, reload: true }).catch(() => null)
      if (freshUser) {
        user.setUserData(freshUser)
      }
    }
    if (!isSuspended.value) {
      const confirmed = await useDialog().alert({
        title: t('suspended_page.cleared_toast'),
        body: t('suspended_page.login_required_desc'),
        confirmButtonText: session.realAdmin ? t('ok') : t('suspended_page.submit_button'),
      })
      if (!session.realAdmin || confirmed === 'confirm') {
        redirectedAway.value = true
        await navigateTo(redirectTarget.value)
      }
      return
    }
    await useDialog().alert({
      title: t('suspended_page.still_suspended_toast'),
      body: t('suspended_page.request_description'),
      confirmButtonText: t('ok'),
    })
  } catch (e) {
    reloadError.value = t('suspended_page.refresh_error')
  } finally {
    refreshing.value = false
  }
}

const submitRequest = async () => {
  submitError.value = ''
  const totalDevices = selectedDeviceIds.value.length + phoneDevices.value.length
  if (!totalDevices) {
    submitError.value = t('suspended_page.validation_devices_any')
    return
  }
  if (!homeCity.value.trim()) {
    submitError.value = t('suspended_page.validation_home_city')
    return
  }
  if (!tosRead.value || !tosSelf.value || !tosViolation.value) {
    submitError.value = t('suspended_page.validation_tos')
    return
  }
  const previewLines = [
    `${t('suspended_page.devices_title')}: ${selectedDeviceIds.value.join(', ')}`,
    `${t('suspended_page.phone_title')}: ${phoneDevices.value
      .map((p) => `${p.manufacturer} ${p.model}`)
      .join(', ')}`,
    `${t('suspended_page.home_city_label')}: ${homeCity.value.trim()}`,
    deviceNotes.value.trim()
      ? `${t('suspended_page.notes_label')}: ${deviceNotes.value.trim()}`
      : '',
  ].filter(Boolean)
  const confirmed = await useDialog().confirm({
    title: t('suspended_page.confirm_title'),
    body: `${t('suspended_page.confirm_description')}\n\n${previewLines.join('\n')}`,
    confirmButtonText: t('suspended_page.confirm_submit'),
    cancelButtonText: t('service_selector.cancel'),
    confirmButtonVariant: 'warning',
  })
  if (confirmed !== 'confirm') {
    return
  }
  submitLoading.value = true
  try {
    await api.post('/unsuspend/requests', {
      body: {
        device_ids: selectedDeviceIds.value,
        phone_tablets: phoneDevices.value,
        notes: deviceNotes.value.trim(),
        home_city: homeCity.value.trim(),
      },
    })
    await useDialog().alert({
      title: t('suspended_page.submit_success'),
      body: previewLines.join('\n'),
      confirmButtonText: t('ok'),
    })
    deviceNotes.value = ''
  } catch (e: any) {
    const raw =
      e?.data?.error ||
      e?.data?.message ||
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      e?.message ||
      ''
    const rawStr = typeof raw === 'string' ? raw : ''
    const lowered = rawStr.toLowerCase()
    if (lowered === 'duplicate' || lowered.includes('duplicate')) {
      const ticketsLink = `/dashboard/${user.id}/tickets`
      submitError.value = `${t('suspended_page.submit_duplicate')} <a href="${ticketsLink}" class="text-primary underline">${t('suspended_page.view_existing')}</a>`
    } else if (rawStr && rawStr.length < 200) {
      submitError.value = rawStr
    } else {
      submitError.value = t('suspended_page.submit_failed')
    }
  } finally {
    submitLoading.value = false
  }
}

</script>

<style scoped lang="postcss">

.suspended-wrap {
  padding: 20px;
  width: 100%;
  /* display: flex; */
  justify-content: center;
}
</style>
